import qs from 'qs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import type { AnyFilter } from '../../shared/organizationPackages/filters';
import { useLocation, useRouter } from '../contexts/RouterContext';
import { flattenFilters, parseQueryParams } from '../helpers/packageInventory';
import * as PackageInventoryStore from '../redux/packageInventory';
import type { HydrateStateQuery } from '../redux/packageInventory/types';

import useDebouncedCallback from './useDebouncedCallback';

const URL_UPDATE_DEBOUNCE_MS = 750;

const parseSearchParams = (search: string): HydrateStateQuery =>
  parseQueryParams(qs.parse(search, { ignoreQueryPrefix: true }));

const usePackageInventorySearchParams = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const router = useRouter();
  const parsedState = useMemo(() => parseSearchParams(location.search ?? ''), [location.search]);
  const pendingStateRef = useRef(parsedState);
  const pathnameRef = useRef(location.pathname);
  //  Filters set since the URL last changed. Ignored once the URL changes, since it then holds the newest state.
  const [localFilters, setLocalFilters] = useState<{ urlState: HydrateStateQuery; filter: AnyFilter } | null>(null);

  const replaceSearchParams = useDebouncedCallback(() => {
    router.replace({
      pathname: pathnameRef.current,
      search: `?${qs.stringify(flattenFilters(pendingStateRef.current))}`,
    });
  }, URL_UPDATE_DEBOUNCE_MS);

  //  Drop any pending write when the location changes, so it can't overwrite the new URL.
  useEffect(() => {
    replaceSearchParams.cancel();
    pathnameRef.current = location.pathname;
    pendingStateRef.current = parsedState;
  }, [location.pathname, parsedState, replaceSearchParams]);

  const setFilters = useCallback((partialFilters: AnyFilter) => {
    const currentState = pendingStateRef.current;
    const nextState = {
      ...currentState,
      page: 1,
      filter: {
        ...currentState.filter,
        ...partialFilters,
      },
    };

    pendingStateRef.current = nextState;
    setLocalFilters({ urlState: parsedState, filter: nextState.filter });
    dispatch(PackageInventoryStore.actions.setFilters(partialFilters));
    replaceSearchParams();
  }, [dispatch, parsedState, replaceSearchParams]);

  const filters = localFilters?.urlState === parsedState ? localFilters.filter : parsedState.filter;

  return {
    filters: filters ?? {},
    setFilters,
  };
};

export default usePackageInventorySearchParams;
